import { useEffect, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";

import MDTypography from "components/MDTypography";
import DashboardLayoutMin from "examples/LayoutContainers/DashboardLayoutMin";
import ActionsMenu from "./ActionsMenu";
import CoursesDialog from "./CoursesDialog";
import AllottedCoursesDialog from "./AllottedCoursesDialog";
function ExamCourseAllotment() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Dropdown states
  const [programs, setPrograms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [yearSemesters, setYearSemesters] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  // Selected values
  const [programId, setProgramId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [yearNumber, setYearNumber] = useState("");
  const [semesterNumber, setSemesterNumber] = useState("");

  const [selectedRow, setSelectedRow] = useState(null);
  const [coursesDialogOpen, setCoursesDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allottedDialogOpen, setAllottedDialogOpen] = useState(false);

  const handleOpenCourses = (row) => {
    setSelectedRow(row);
    setCoursesDialogOpen(true);
  };
  const handleOpenAllottedCourses = (row) => {
    setSelectedRow(row);
    setAllottedDialogOpen(true);
  };
  // Grid data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return {
      Authorization: `Bearer ${token}`,
    };
  };
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/programs/${clientId}/${collegeId}`, {
          headers: getAuthHeaders(),
        });
        setPrograms(response.data);
      } catch (error) {
        console.error("Error fetching programs:", error);
      }
    };

    fetchPrograms();
  }, [clientId, collegeId]);

  // Fetch admitted batches when program changes
  useEffect(() => {
    const fetchBatches = async () => {
      if (programId) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/admittedbatches/list/${clientId}/${collegeId}/${programId}`,
            { headers: getAuthHeaders() }
          );

          const normalized = response.data.map((row) => ({
            admittedbatch_id: row.admittedbatch_id,
            admittedbatch_name: row.admittedbatch?.admittedbatch || "Unknown Batch",
          }));

          setBatches(normalized);
        } catch (error) {
          console.error("Error fetching batches:", error);
        }
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/program-year-semesters/${clientId}/${collegeId}/${programId}`,
            { headers: { "Cache-Control": "no-cache" } }
          );

          setYearSemesters(response.data.data.items || []);
        } catch (err) {
          console.error("Error fetching year/semesters", err);
        }
      }
    };

    fetchBatches();
  }, [programId, clientId]);
  useEffect(() => {
    const fetchRegulations = async () => {
      if (batchId) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/admittedbatches/regulations/${clientId}/${collegeId}/${batchId}`,
            {
              headers: {
                ...getAuthHeaders(),
                "Cache-Control": "no-cache",
              },
            }
          );

          setRegulations(response.data.data || []);
        } catch (error) {
          console.error("Error fetching regulations:", error);
        }
      }
    };

    fetchRegulations();
  }, [batchId]);

  useEffect(() => {
    if (yearNumber) {
      const yearNum = Number(yearNumber);
      const sems = yearSemesters.filter((ys) => ys.year_number === yearNum);
      setSemesters(sems);
    }
  }, [yearNumber, yearSemesters]);

  // Search branches
  const handleSearch = async () => {
    if (!batchId || !yearNumber || !semesterNumber) return;
    setLoading(true);
    try {
      const apiUrl = programId
        ? `${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}/${programId}/${batchId}/${yearNumber}/${semesterNumber}`
        : `${API_BASE_URL}/api/admittedbatches/${clientId}/${collegeId}/all/${batchId}/${yearNumber}/${semesterNumber}`;

      const res = await axios.get(apiUrl, {
        headers: {
          ...getAuthHeaders(),
          "Cache-Control": "no-cache",
        },
      });

      const normalized = res.data.data.map((row) => {
        const reg = regulations.find((r) => r.admittedbatch_id === row.admittedbatch_id);
        return {
          ...row,
          regulation_id: reg?.regulation_id || "",
          regulation_code: reg?.regulation_code || "",
          regulation_name: reg?.regulation_name || "",
          year_number: yearNumber || "",
          semester_number: semesterNumber || "",
        };
      });

      setRows(normalized);
    } catch (err) {
      console.error("Error fetching branches", err);
    }
    setLoading(false);
  };

  const columns = [
    {
      field: "sno",
      headerName: "SNo",
      width: 70,
      sortable: false,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },

    {
      field: "batch_name",
      headerName: "Admitted Batch",
      width: 150,
      valueGetter: (params) => params.row.admittedbatch?.admittedbatch || "",
    },
    {
      field: "program_branch_regulation",
      headerName: "Program / Branch / Regulation",
      width: 300,
      renderCell: (params) => {
        const program = params.row.program?.program_name || "";
        const branch = params.row.branch?.branch_name || "";
        const regulation = params.row.regulation_code || "";
        return (
          <div style={{ whiteSpace: "pre-line", lineHeight: 1.4 }}>
            {`${program}\n${branch}\n${regulation}`}
          </div>
        );
      },
    },

    { field: "branch_id", headerName: "Branch ID", width: 100 },

    {
      field: "student_count",
      headerName: "Students",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "course_count",
      headerName: "Courses",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "allotted_count",
      headerName: "Allotted",
      width: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <ActionsMenu
          row={params.row}
          user={{
            college_id: user.college_id,
            client_id: user.client_id,
            user_email: user.user_email,
          }}
          onOpenCourses={handleOpenCourses}
          onOpenAllottedCourses={handleOpenAllottedCourses}
          showSnackbar={showSnackbar}
        />
      ),
    },
  ];

  return (
    <DashboardLayoutMin>
      <MDBox pt={1} pb={3}>
        <Card style={{ padding: 15 }}>
          <MDTypography variant="h6" gutterBottom>
            Assign Courses to Students
          </MDTypography>

          <Grid container pt={2} spacing={2} mb={2} alignItems="center">
            <Grid item xs={2.2}>
              <TextField
                label="Program"
                name="program_id"
                select
                fullWidth
                value={programId}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                onChange={(e) => {
                  setProgramId(e.target.value);
                  setBatchId(""); // reset batch when program changes
                }}
              >
                <option value="">Select Program</option>
                {programs.map((p) => (
                  <option key={p.program_id} value={p.program_id}>
                    {p.program_name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={2.2}>
              <TextField
                label="Admitted Batch"
                name="admittedbatch_id"
                select
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                value={batchId}
                onChange={(e) => setBatchId(e.target.value)}
              >
                <option value="">Select AdmittedBatch</option>
                {batches.map((b) => (
                  <option key={b.admittedbatch_id} value={b.admittedbatch_id}>
                    {b.admittedbatch_name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={2.2}>
              <TextField
                label="Year Number"
                name="year_number"
                select
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                value={yearNumber}
                onChange={(e) => setYearNumber(e.target.value)}
              >
                <option value="">Select Year</option>
                {[...new Set(yearSemesters.map((ys) => ys.year_number))].map((year, idx) => (
                  <option key={idx} value={year}>
                    {year}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={2.2}>
              <TextField
                label="Semester"
                name="semester_number"
                select
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                value={semesterNumber}
                onChange={(e) => setSemesterNumber(e.target.value)}
              >
                <option value="">Select Semester</option>
                {semesters.map((sem, idx) => (
                  <option key={idx} value={sem.semester_number}>
                    {sem.semester_number}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={1.2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  backgroundColor: "#ff9800",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#c77700" },
                }}
              >
                {loading ? "Loading..." : "Search"}
              </Button>
            </Grid>
          </Grid>

          {/* DataGrid */}
          <MDBox mt={1} sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id || row.admittedbatch_branch_id || Math.random()}
              pageSize={pageSize} // controlled pageSize
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              disableRowSelectionOnClick
              pagination
              disableSelectionOnClick
              disableColumnMenu
              getRowHeight={() => "auto"}
            />
          </MDBox>
        </Card>
      </MDBox>
      <CoursesDialog
        open={coursesDialogOpen}
        programs={programs}
        onClose={() => setCoursesDialogOpen(false)}
        row={selectedRow}
        user={user}
        showSnackbar={showSnackbar}
      />
      <AllottedCoursesDialog
        open={allottedDialogOpen}
        onClose={() => setAllottedDialogOpen(false)}
        row={selectedRow}
        user={user}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayoutMin>
  );
}

export default ExamCourseAllotment;
